import { ApiBackendError } from '@/teoCore/errors/ApiError';
import type { PWResetResult, SubscriptionConfig } from '@/teoCore/TeoApi';
import type { DropDownLoginItem } from '@/ui/elements/dropdown/DropDownLogin/DropDownLoginSelect.svelte';
import { isValidEmail, isValidPassword } from '@/utils/validators';

import { isApiBackendError } from '../../LoginElement.svelte';
import { useAuth } from '../../use-auth.svelte';
import {
  ERROR_EMAIL_FORMAT,
  ERROR_LOGIN_FAILED,
  ERROR_PASSWORD_FORMAT,
  ERROR_RESET_SEND_MAIL,
  ERROR_USER_SELECTION_REQUIRED,
  LABELS_LOGIN_MODE,
  LABELS_RESET_CONFIRM_MODE,
  LABELS_RESET_MODE,
} from './login-constants';

type InputEvent = { currentTarget: { value: string } };

export class LoginFormState {
  loading = $state(false);
  mode = $state<'login' | 'reset' | 'resetconfirm'>('login');
  userInputType: 'input' | 'select' | 'both' | undefined = $state(undefined);
  showUserInputTypeToggle = $derived(this.userInputType === 'both' && this.mode === 'login');
  currentUserInputType = $state<'input' | 'select'>('input');
  labels = $state(LABELS_LOGIN_MODE);
  formError = $state('');
  email = new FormElementState();
  password = new FormElementState();
  userSelection = new FormSelectState();

  constructor(authState: ReturnType<typeof useAuth>[0], config: SubscriptionConfig) {
    if (authState.current.error) {
      this.password.error = ERROR_LOGIN_FAILED;
    }
    const useNamedUsers = config.use_named_users;
    const useGenericUsers = config.use_generic_users && !!config.generic_users?.length;
    this.userInputType =
      useGenericUsers ?
        useNamedUsers ? 'both'
        : 'select'
      : 'input';

    this.currentUserInputType =
      this.userInputType === 'both' ?
        __DEV__ ? 'input'
        : 'select'
      : this.userInputType;

    if (useGenericUsers) {
      this.userSelection.options =
        config.generic_users?.map((user) => ({
          label: user.name,
          value: user.email,
          disabled: !!user.is_logged_in_api,
        })) ?? [];
    }

    $effect(() => {
      this.email.visible = this.mode !== 'resetconfirm' && this.currentUserInputType === 'input';
      this.userSelection.visible =
        this.mode !== 'resetconfirm' && this.currentUserInputType === 'select';
      this.password.visible = this.mode === 'login';
    });
  }

  getCredentials() {
    if (this.currentUserInputType === 'input') {
      return { login: this.email.value, password: this.password.value };
    } else {
      return { login: this.userSelection.value, password: this.password.value };
    }
  }

  resetErrors() {
    this.email.error = '';
    this.password.error = '';
    this.userSelection.error = '';
    this.formError = '';
  }

  clearForm() {
    this.email.value = '';
    this.password.value = '';
    this.userSelection.value = '';
    this.resetErrors();
  }
}

export function useLoginForm({ config }: { config: SubscriptionConfig }) {
  const [authState, { logIn, requestPasswortReset }] = useAuth();
  const state = new LoginFormState(authState, config);

  const actions = {
    handleEmailInput: (e: InputEvent | string) => {
      state.email.value = typeof e === 'string' ? e : e.currentTarget.value;
      state.email.error = '';
    },
    handleEmailChange: validateEmail,

    handlePasswordInput: (e: InputEvent) => {
      state.password.value = e.currentTarget.value;
      state.password.error = '';
    },
    handlePasswordChange: validatePassword,

    handleUserSelectionChange: (e: InputEvent | string) => {
      state.userSelection.value = typeof e === 'string' ? e : e.currentTarget.value;
      state.userSelection.error = '';
    },

    setMode,
    toggleMode() {
      switch (state.mode) {
        case 'login':
          setMode('reset');
          break;
        case 'reset':
          setMode('login');
          break;
        case 'resetconfirm':
          setMode('reset');
          break;
      }
    },

    toggleUserInputType() {
      if (state.userInputType === 'both') {
        state.currentUserInputType = state.currentUserInputType === 'input' ? 'select' : 'input';
        state.clearForm();
      }
    },

    validate() {
      state.resetErrors();
      const passwordValid = state.mode !== 'login' || validatePassword();
      const loginValid =
        state.currentUserInputType === 'input' ? validateEmail() : validateUserSelection();
      return loginValid && passwordValid;
    },

    async submit(e: Event) {
      e.preventDefault();
      if (actions.validate()) {
        switch (state.mode) {
          case 'login':
            return await login();
          case 'reset':
            return await resetPassword();
          case 'resetconfirm':
            state.resetErrors();
            setMode('login');
        }
      }
      return false;
    },
  };

  async function login() {
    try {
      state.loading = true;
      state.resetErrors();
      const credentials = state.getCredentials();
      await logIn(credentials.login, credentials.password);
      if (authState.current.error) {
        state.formError = ERROR_LOGIN_FAILED;
        state.loading = false;
        return false;
      }
      return true;
    } catch {
      state.formError = ERROR_LOGIN_FAILED;
      state.loading = false;
    }
    return false;
  }

  async function resetPassword() {
    try {
      state.loading = true;
      state.resetErrors();

      const credentials = state.getCredentials();
      await requestPasswortReset(credentials.login).then(
        (value: PWResetResult | ApiBackendError) => {
          if (isApiBackendError(value) || !value.success) {
            state.formError = ERROR_RESET_SEND_MAIL;
          } else {
            state.formError = '';
            setMode('resetconfirm');
          }
        },
      );
      return true;
    } catch {
      state.formError = ERROR_RESET_SEND_MAIL;
    } finally {
      state.loading = false;
    }
    return false;
  }

  function setMode(mode: 'login' | 'reset' | 'resetconfirm') {
    state.mode = mode;
    state.resetErrors();
    switch (mode) {
      case 'login':
        state.labels = LABELS_LOGIN_MODE;
        break;
      case 'reset':
        state.labels = LABELS_RESET_MODE;
        state.password.value = '';
        break;
      case 'resetconfirm':
        state.labels = LABELS_RESET_CONFIRM_MODE(state.email.value);
        break;
    }
  }

  function validateEmail() {
    if (!isValidEmail(state.email.value)) {
      state.email.error = ERROR_EMAIL_FORMAT;
      return false;
    }
    return true;
  }

  function validatePassword() {
    if (!isValidPassword(state.password.value)) {
      state.password.error = ERROR_PASSWORD_FORMAT;
      return false;
    }
    return true;
  }

  function validateUserSelection() {
    if (!state.userSelection.value) {
      state.userSelection.error = ERROR_USER_SELECTION_REQUIRED;
      return false;
    }
    return true;
  }

  return [state, actions] as const;
}

class FormElementState {
  value = $state('');
  error = $state('');
  visible = $state(true);
}

class FormSelectState extends FormElementState {
  options = $state<DropDownLoginItem[]>([]);
}
